import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { Redirect, Route, Switch } from 'wouter';
import { useTranslation } from 'react-i18next';
import './i18n.ts';
import './styles.css';
import './app.css';
import { api, ApiError, type Me } from './api.ts';
import { Layout } from './components/Layout.tsx';
import { DemoLanding } from './pages/DemoLanding.tsx';
import { Login } from './pages/Login.tsx';
import { Devices } from './pages/Devices.tsx';
import { Configurator } from './pages/Configurator.tsx';
import { Connections } from './pages/Connections.tsx';
import { Settings } from './pages/Settings.tsx';
import { Guide } from './pages/Guide.tsx';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2, refetchOnWindowFocus: false } },
});

function App() {
  const { t } = useTranslation();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api<Me>('/api/me'), retry: false });
  if (me.isLoading) return <Layout me={null}><p className="muted">{t('common.loading')}</p></Layout>;
  const user = me.data ?? null;

  if (!user) {
    return (
      <Layout me={null}>
        <Switch>
          <Route path="/demo"><DemoLanding /></Route>
          <Route path="/login"><Login /></Route>
          <Route><Redirect to="/login" /></Route>
        </Switch>
      </Layout>
    );
  }

  return (
    <Layout me={user}>
      <Switch>
        <Route path="/"><Devices me={user} /></Route>
        <Route path="/demo"><Redirect to="/" /></Route>
        <Route path="/login"><Redirect to="/" /></Route>
        <Route path="/devices/:id">{(p) => <Configurator id={p.id} me={user} />}</Route>
        <Route path="/connections"><Connections me={user} /></Route>
        <Route path="/settings"><Settings me={user} /></Route>
        <Route path="/guides/:slug">{(p) => <Guide slug={p.slug} />}</Route>
        <Route><p>{t('common.notFound')}</p></Route>
      </Switch>
    </Layout>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);
